import {BehaviorSubject} from "rxjs";
import {MessageProvider} from "../PA.Core/Basic/Messages";
import {IMessageProvider} from "../PA.Core/PA.Core.IF.Basic";
import {PAdbContext} from "../PA.Core/PAdbContext";
import {WB_AttributeTypeRow, WB_CompanyRow, WB_EntityRow} from "../PA.Core/PAdbSchemaRows";
import {
    IEntity,
    IEntityAttribute,
    IEntityAttributeChangeEvent,
    IEntityAttributeSearchOptions
} from "../PA.MD.Core/PA.MD.IF.Entity";
import * as _ from 'lodash'
import {ESRecord} from "../PA.Core/lib/ESRecord";
import {esWB_Company} from "../PA.Core/PAdbSchema";
import {EntityTypeUtils} from "./EntityTypeUtils";
import {CoreAttribute} from "./CoreAttribute";
import {EntityUtils} from "./EntityUtils";

export class CompanyEntity implements IEntity {

    constructor(companyRow: WB_CompanyRow) {
        this.companyRow = companyRow;
        this._dbContext = <PAdbContext>companyRow.dataStore();
        this.loadMessageProvider = new MessageProvider()
        this.validateMessageProvider = new MessageProvider()
        this._bLoaded = false
        this._bSaved = false
        this._bDeleted = false
        this.onLoadComplete = undefined
        this.dctAttributeMById = {}
        this.dctAttributeMByWbId = {}
        this.dctAttributesByAttrMId = {}
        this.entityAttributes = []
    }

    private companyRow: WB_CompanyRow
    private _dbContext: PAdbContext
    private loadMessageProvider: MessageProvider
    private entityAttributes: IEntityAttribute[];
    private validateMessageProvider: MessageProvider
    private _bLoaded: boolean
    private _bDeleted: boolean;
    private _bSaved: boolean;
    private onLoadComplete: Promise<void>

    private dctAttributeMByWbId: { [wbId: number]: WB_AttributeTypeRow }
    private dctAttributeMById: { [attrMId: string]: WB_AttributeTypeRow }
    private dctAttributesByAttrMId: { [attrMId: string]: IEntityAttribute[] }

    get dbContext(): PAdbContext {
        return this._dbContext
    }

    get wbMasterDataSetId(): number {
        return this.companyRow.cif.WB_MDSET_ID
    }

    get wb_id(): number {
        return this.companyRow.cif.WB_COMPANY_ID
    }

    get id_ui(): string {
        return this.companyRow.cif.COMPANY_ID
    }

    get key_set(): string {
        return this.companyRow.cif.ENTERPRISE_SET_ID
    }

    get name(): string {
        return this.companyRow.cif.COMPANY_NAME
    }

    get mapperName(): string {
        return esWB_Company
    }

    get LoadMessageProvider(): IMessageProvider {
        return this.loadMessageProvider
    }

    get EntityAttributesChanged(): BehaviorSubject<IEntityAttributeChangeEvent> {
        return this.EntityAttributesChanged;
    }

    OnLoadComplete(): Promise<void> {
        const self = this;
        if (!_.isUndefined(this.onLoadComplete))
            return this.onLoadComplete

        let rv = (async () => {

            try {
                let coreAttrs: WB_AttributeTypeRow[] = await EntityTypeUtils.getCoreAttributeTypes("Enterprise");

                _.forEach(coreAttrs, (a) => {
                    if (a.cif.CORE_ROWMAPPER != esWB_Company)
                        return //enterprise types can be company, sales area, or sales org. make sure only using correct one

                    let wbId = a.cif.WB_ATTRIBUTEM_ID
                    let Id = a.cif.ATTRIBUTEM_ID
                    if (!(wbId in self.dctAttributeMByWbId))
                        self.dctAttributeMByWbId[wbId] = a

                    if (!(Id in self.dctAttributeMById))
                        self.dctAttributeMById[Id] = a;
                })

                // Create core attributes
                for (let coreAttrM of coreAttrs) {
                    //use the path to determine the datarecords
                    let startMapper = coreAttrM.cif.CORE_STARTMAPPER
                    let rowMapper = coreAttrM.cif.CORE_ROWMAPPER
                    let rowFieldName = coreAttrM.cif.CORE_VALUE_FIELDNAME
                    let path = coreAttrM.cif.CORE_PATH.trim()
                    if (startMapper != esWB_Company || rowMapper != esWB_Company)
                        continue //if it's not this mapper, then it's a custom attribute, which shouldn't be allowed
                    let tgtEntity: WB_EntityRow = undefined
                    if (coreAttrM.cif.DATATYPE == "entity") {
                        tgtEntity = await EntityTypeUtils.getEntityTypeRowByLogicalKey(self.wbMasterDataSetId, coreAttrM.cif.ENTITY_DIMENSION, coreAttrM.cif.CORE_ENTITY_ID_TARGET)
                    }

                    if ((rowMapper == startMapper) && _.isEmpty(path)) {
                        let ca = new CoreAttribute(self, coreAttrM, self.companyRow, tgtEntity, rowFieldName)
                        self.entityAttributes.push(ca)
                        this.indexAttribute(ca)
                        continue
                    }

                    //find data rows related to path
                    let arrPathItems = path.split(".")

                    function findRows(searchRows: ESRecord[], pPath: string[]): ESRecord[] {
                        if (_.isEmpty(pPath))
                            return searchRows
                        let nextRows: ESRecord[] = []
                        let fieldName = pPath.shift()
                        _.forEach(searchRows, (a) => {
                            let fieldVal = (<any>a)[fieldName]
                            if (_.isArray(fieldVal)) {
                                _.forEach(fieldVal, (aRow: ESRecord) => {
                                    if (!_.isObject(aRow))
                                        return
                                    if (aRow.isActive)
                                        nextRows.push(aRow)
                                })
                            } else if (_.isObjectLike(fieldVal)) {
                                if (!fieldVal.isActive)
                                    return
                                nextRows.push(fieldVal)
                            } else {
                                // Element not found
                            }
                        })

                        return findRows(nextRows, _.clone(pPath))
                    }

                    let foundRows = findRows([self.companyRow], arrPathItems)
                    for (let aRow of foundRows) {
                        let aCoreAttr = new CoreAttribute(self, coreAttrM, aRow, tgtEntity,
                            coreAttrM.cif.CORE_VALUE_FIELDNAME,
                            coreAttrM.cif.CORE_SALESAREA_FIELDNAME,
                            coreAttrM.cif.CORE_STARTDT_FIELDNAME,
                            coreAttrM.cif.CORE_ENDDT_FIELDNAME)
                        self.entityAttributes.push(aCoreAttr)
                        this.indexAttribute(aCoreAttr)

                    }
                }

                self._bLoaded = true
            } catch (err) {
                self._bLoaded = false
            }

        })();

        return rv;
    }

    private indexAttribute(ea: IEntityAttribute): void {
        if (_.isEmpty(ea) || _.isEmpty(ea.type))
            return

        let attrMId = ea.type.cif.ATTRIBUTEM_ID
        if (!(attrMId in this.dctAttributesByAttrMId))
            this.dctAttributesByAttrMId[attrMId] = []

        this.dctAttributesByAttrMId[attrMId].push(ea)
    }

    get bLoaded(): boolean {
        return this._bLoaded
    }

    get EntityAttributes(): IEntityAttribute[] {
        return this.entityAttributes
    }

    get AttributeTypes(): WB_AttributeTypeRow[] {
        return _.values(this.dctAttributeMByWbId)
    }

    async createAttribute(_attrType: number | WB_AttributeTypeRow): Promise<IEntityAttribute> {
        throw new Error("Company is not a custom entity, cannot create attributes")
    }

    getEntityAttribute(attributeTypeId: string, options?: IEntityAttributeSearchOptions): IEntityAttribute {
        return EntityUtils.searchAttributes(this,
            this.dctAttributesByAttrMId[attributeTypeId] || [],
            options)
    }

    get ValidateMessageProvider(): MessageProvider {
        return this.validateMessageProvider
    };

    async Validate(): Promise<void> {
        return undefined
    }

    async Save(bBatch?: boolean, options?: { bSkipValidations?: boolean; }): Promise<void> {
        this._bSaved = true
    }

    get bSaved(): boolean {
        return this._bSaved
    }

    async Delete(_bSaveNow: boolean, _options?: { bSkipValidations?: boolean; }): Promise<void> {
        //only delete through the BO
        this._bDeleted = false
    }

    get bDeleted(): boolean {
        return this._bDeleted
    }

    setName(sName: string) {
        this.companyRow.cif.COMPANY_NAME = sName
    }

    get bHasSalesAreas(){
        return false
    }
}
